import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  width?: number;
  children: ReactNode;
  /** When true, the drawer renders inline (co-exists with list) rather than slid over. */
  inline?: boolean;
  className?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  header,
  footer,
  width = 520,
  children,
  inline = false,
  className = '',
}: DrawerProps) {
  useEffect(() => {
    if (!open || inline) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, inline]);

  if (!open) return null;

  if (inline) {
    return (
      <aside className={`ds-drawer ds-drawer-inline ${className}`.trim()}>
        {header ? (
          <div className="ds-drawer-header">{header}</div>
        ) : title ? (
          <div className="ds-drawer-header">
            <div className="ds-drawer-title">{title}</div>
            <button
              type="button"
              className="ds-btn ds-btn-ghost ds-btn-icon ds-btn-sm"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        ) : null}
        <div className="ds-drawer-body">{children}</div>
        {footer ? <div className="ds-drawer-footer">{footer}</div> : null}
      </aside>
    );
  }

  return (
    <>
      <div className="ds-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className={`ds-drawer ds-drawer-slide ${className}`.trim()}
        style={{ width }}
        role="dialog"
        aria-modal="true"
      >
        {header ? (
          <div className="ds-drawer-header">{header}</div>
        ) : title ? (
          <div className="ds-drawer-header">
            <div className="ds-drawer-title">{title}</div>
            <button
              type="button"
              className="ds-btn ds-btn-ghost ds-btn-icon ds-btn-sm"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        ) : null}
        <div className="ds-drawer-body">{children}</div>
        {footer ? <div className="ds-drawer-footer">{footer}</div> : null}
      </aside>
    </>
  );
}

export default Drawer;
